import { faCircleExclamation, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { type Row, type Table, flexRender } from '@tanstack/react-table';
import { type VariantProps } from 'class-variance-authority';
import { useIntl } from 'react-intl';

import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { TableCell, tableCellVariants, TableRow } from 'src/registry/ui/Table';

import type { DataTableSemantics } from 'src/registry/components/table/types';

export interface DataTableBodyProps<TData> extends VariantProps<typeof tableCellVariants> {
    table: Table<TData>;
    semantics?: DataTableSemantics;
    columnLength: number;
    isLoading?: boolean;
    isError?: boolean;
    errorUpdateCount?: number;
    onRowClickCallback?: (row: TData) => void;
    enableRowClick?: boolean;
}

export function DataTableBody<TData>({
    table,
    semantics,
    isLoading,
    isError,
    columnLength,
    errorUpdateCount,
    onRowClickCallback,
    enableRowClick,
    ...rest
}: DataTableBodyProps<TData>) {
    const { formatMessage } = useIntl();

    const onRowClick = (row: Row<TData>) => {
        if (!enableRowClick) return; // disable row click for multi-select tables

        if (onRowClickCallback) {
            onRowClickCallback(row.original);
        } else {
            row.toggleSelected();
        }
    };

    if (isLoading && !errorUpdateCount) {
        return Array(table.getState().pagination.pageSize)
            .fill('')
            .map((_, i) => (
                <TableRow key={i} className="sticky top-0">
                    {table.getVisibleFlatColumns().map((cell) => (
                        <TableCell key={cell.id} className={cell.columnDef.id === 'index' ? 'font-mono' : undefined}>
                            {flexRender(
                                <Skeleton className="my-2 h-4 w-full max-w-24">
                                    {cell.columnDef.id === 'index' && <span className="opacity-0">0</span>}
                                </Skeleton>,
                                {},
                            )}
                        </TableCell>
                    ))}
                </TableRow>
            ));
    }

    if (isError || (errorUpdateCount && !table.getRowModel().rows?.length)) {
        return (
            <TableRow className="sticky top-0">
                <TableCell className="group-hover:bg-white" style={{ gridColumn: `1 / span ${columnLength}` }}>
                    <GeneralMessageBlock className="py-12" variant="error" icon={faCircleExclamation} />
                </TableCell>
            </TableRow>
        );
    }

    if (!table.getRowModel().rows?.length) {
        const messages = { title: tableMessages.noResults, subtitle: tableMessages.noResultsSubtitle };

        if (table.getState().globalFilter != null && table.getState().globalFilter !== '') {
            messages.subtitle = tableMessages.noResultsSubtitleSearch;
        } else if (table.getState().columnFilters.length > 0) {
            messages.subtitle = tableMessages.noResultsSubtitleFilters;
        } else {
            messages.subtitle = tableMessages.noResultsSubtitle;
        }

        // const subtitle = table.getState().globalFilter != null ? tableMessages.noResultsSubtitle : undefined;
        return (
            <TableCell
                className="inset-0 m-auto group-hover:bg-white"
                style={{ gridColumn: `1 / span ${columnLength}` }}
            >
                <GeneralMessageBlock
                    className="py-12"
                    icon={faSearch}
                    title={formatMessage(messages.title)}
                    subtitle={formatMessage(messages.subtitle, {
                        search: table.getState().globalFilter as string,
                        entries:
                            (semantics?.messageBlockSubtitle?.toLocaleLowerCase() as string) ??
                            (semantics?.entryNames?.toLocaleLowerCase() as string) ??
                            formatMessage(tableMessages.entries).toLocaleLowerCase(),
                    })}
                />
            </TableCell>
        );
    }

    return table.getRowModel().rows.map((row) => {
        return (
            <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? 'selected' : false}
                selectable={Boolean(!table.options.enableMultiRowSelection)}
                onClick={() => onRowClick(row)}
            >
                {row.getVisibleCells().map((cell) => (
                    <TableCell
                        key={cell.id}
                        cellVerticalPadding={rest.cellVerticalPadding}
                        cellHorizontalPadding={rest.cellHorizontalPadding}
                    >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                ))}
            </TableRow>
        );
    });
}
